// Small pieces the shell and the renderers share: a labelled icon button, the
// toolbar rows, the notice card an unsupported file gets, and the family label.

import type { ReactNode, MouseEvent } from 'react';
import { ActionIcon } from '../core/ActionIcon/ActionIcon.js';
import { Button } from '../core/Button/Button.js';
import { Tooltip } from '../overlays/Tooltip/Tooltip.js';
import type { FileViewerLabels } from './labels.js';
import type { FileFamily } from './types.js';
import { FamilyIcon } from './icons.js';

export function IconButton({ label, onClick, children, active, disabled, size = 'md', pressed, className }: {
  label: string;
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  children: ReactNode;
  active?: boolean;
  disabled?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** For a toggle: the aria-pressed state. */
  pressed?: boolean;
  className?: string;
}): ReactNode {
  return (
    <Tooltip label={label} position="bottom" openDelay={400}>
      <ActionIcon
        type="button"
        variant={active ? 'light' : 'subtle'}
        size={size}
        aria-label={label}
        aria-pressed={pressed}
        disabled={disabled}
        onClick={onClick}
        className={['rr-fv__iconbtn', active && 'rr-fv__iconbtn--active', className].filter(Boolean).join(' ')}
      >
        {children}
      </ActionIcon>
    </Tooltip>
  );
}

export function Toolbar({ children, className }: { children: ReactNode; className?: string }): ReactNode {
  return <div className={['rr-fv__toolbar', className].filter(Boolean).join(' ')} role="toolbar">{children}</div>;
}

export function ToolbarGroup({ children, className }: { children: ReactNode; className?: string }): ReactNode {
  return <div className={['rr-fv__toolbar-group', className].filter(Boolean).join(' ')}>{children}</div>;
}

export function ToolbarText({ children, mono }: { children: ReactNode; mono?: boolean }): ReactNode {
  return <span className={['rr-fv__toolbar-text', mono && 'rr-fv__toolbar-text--mono'].filter(Boolean).join(' ')}>{children}</span>;
}

export function familyLabel(labels: FileViewerLabels, family: FileFamily): string {
  switch (family) {
    case 'pdf': return labels.familyPdf;
    case 'image': return labels.familyImage;
    case 'video': return labels.familyVideo;
    case 'audio': return labels.familyAudio;
    case 'spreadsheet': return labels.familySpreadsheet;
    case 'presentation': return labels.familyPresentation;
    case 'document': return labels.familyDocument;
    case 'code': return labels.familyCode;
    case 'text': return labels.familyText;
    case 'archive': return labels.familyArchive;
    default: return labels.familyUnknown;
  }
}

/**
 * The card a file gets when this browser cannot draw it: what it is, why, and
 * the two actions that always work. Renderers add their own sections below.
 */
export function Notice({ family, title, message, labels, download, openExternally, children }: {
  family: FileFamily;
  title: string;
  message: ReactNode;
  labels: FileViewerLabels;
  download?: () => void;
  openExternally?: (() => void) | null;
  children?: ReactNode;
}): ReactNode {
  return (
    <div className="rr-fv__notice-wrap">
      <div className="rr-fv__notice" role="status">
        <div className={`rr-fv__notice-icon rr-fv__tile rr-fv__tile--${family}`}><FamilyIcon family={family} size={30} /></div>
        <div className="rr-fv__notice-title">{title}</div>
        <div className="rr-fv__notice-message">{message}</div>
        <div className="rr-fv__notice-actions">
          {download && <Button size="sm" variant="filled" onClick={download}>{labels.downloadOriginal}</Button>}
          {openExternally && <Button size="sm" variant="default" onClick={openExternally}>{labels.openExternally}</Button>}
        </div>
        <div className="rr-fv__notice-foot">{labels.originalKept}</div>
      </div>
      {children}
    </div>
  );
}
